import React, { FC, useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import AdminDelegateModal from 'dashboard409a/pages/Information/CurrentRequestedDocumentsAndQuestions/AdminDelegateModal';
import { DocumentProgress, QuestionProgress } from 'dashboard409a/pages/Information/CurrentRequestedDocumentsAndQuestions/types';
import { TaskProgressModalContentBody } from 'pages/Funds/components/ProcessManagement/TaskProgressModalContentBody';
import { TaskProgressModalContentHeader } from 'pages/Funds/components/ProcessManagement/TaskProgressModalContentHeader';
import { getObjectValue } from 'utilities';
import { TaskProgressModalContentProps } from './types';

const TaskProgressModalContent: FC<TaskProgressModalContentProps> = props => {
  const { measurementDateName, percentageOfProgress, taskDescription, taskProgressDetails, companySlug, isDocuments, onDelegationSuccess }
    = props;
  const { last_interaction: lastInteraction, id: taskId, category, progress, is_done: isDone } = getObjectValue(taskProgressDetails);

  const [isDelegateModalOpen, setIsDelegateModalOpen] = useState(false);

  // Category 1 = documents, Category 2 = questionnaires
  const isDocumentTask = category === 1;

  // Transform progress data to DocumentProgress format for delegation modal
  const documentProgressData = useMemo((): DocumentProgress[] => {
    if (!isDocumentTask || !progress) {
      return [];
    }
    return progress.map(item => ({
      id: item.id ?? 0,
      name: item.name ?? '',
      // Cast files to expected type - structure is compatible for delegation purposes
      files: (item.files ?? []) as unknown as DocumentProgress['files'],
      is_uploaded: item.is_uploaded ?? false,
      tags: item.tags ?? null,
      created_at: item.created_at?.toString() ?? '',
      updated_at: item.updated_at?.toString() ?? '',
    }));
  }, [isDocumentTask, progress]);

  // For questionnaire tasks, transform to QuestionProgress format
  const questionProgressData = useMemo((): QuestionProgress[] => {
    if (isDocumentTask || !progress) {
      return [];
    }
    return progress.map(item => ({
      id: item.id ?? 0,
      name: item.name ?? '',
      children_questions: item.children_questions ?? [],
      question_type: item.question_type,
      options: item.options ?? [],
      answers: item.answers ?? [],
      parent_question: item.parent_question ?? null,
      created_at: item.created_at ?? new Date(),
      updated_at: item.updated_at,
    }));
  }, [isDocumentTask, progress]);

  // Only show delegate button for document tasks that aren't complete
  // (questionnaire-only tasks would need a separate endpoint)
  const canDelegate = isDocumentTask && !isDone && percentageOfProgress < 100;

  const handleDelegationSuccess = () => {
    setIsDelegateModalOpen(false);
    onDelegationSuccess?.();
  };

  return (
    <>
      <TaskProgressModalContentHeader
        lastInteraction={lastInteraction}
        measurementDateName={measurementDateName}
        percentageOfProgress={percentageOfProgress}
        taskDescription={taskDescription}
        companySlug={companySlug}
        isDocuments={isDocuments}
      />

      <TaskProgressModalContentBody taskProgressDetails={taskProgressDetails} />

      {canDelegate && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setIsDelegateModalOpen(true)}
          >
            Delegate
          </Button>
        </Box>
      )}

      <AdminDelegateModal
        open={isDelegateModalOpen}
        onClose={() => setIsDelegateModalOpen(false)}
        onSuccess={handleDelegationSuccess}
        taskId={taskId}
        requestedFiles={documentProgressData}
        requestedQuestions={questionProgressData}
        skipQuestionnaire
      />
    </>
  );
};

export default TaskProgressModalContent;
